import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Input, Button, Form, Tooltip, Drawer, TreeSelect, Select, Tag, Spin, message, Switch } from 'antd';
import { FilterOutlined, LoadingOutlined, ArrowRightOutlined, CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import * as Utilities from '../../Utilities';
import * as Styles from '../../../../Styles';
import FileConstants from '../../../../FileConstants';
import StyleCategory from '../../FormUtilities/StyleCategorySelect';
import ColorSelect from '../../FormUtilities/ColorSelect';
import './SearchItem.css';
import ProductsList from "../../CustomerComponents/SwapProduct/ProductsList";
import DottedLoader from "../../DottedLoader";

const categoryStyle = Styles.categoryStyle;
const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
        color: "black",
      }}
      spin
    />
  );

const ProductSearch = () => {
    const pageSize = 100;
    const [searchValue, setSearchValue] = useState('');
    const [filtersDrawer, setFiltersDrawer] = useState(false);
    const [productCategoriesData, setProductCategoriesData] = useState([]);
    const [productParentCateogries, setProductParentCateogries] = useState([]);
    const [productCategoryValue, setProductCategoryValue] = useState([]);
    const [productCategoryValueWithChilds, setProductCategoryValueWithChilds] = useState([]);
    const [categorySearchValue, setCategorySearchValue] = useState([]);
    const [productStyleCategory, setProductStyleCategory] = useState([]);
    const [colorValue, setColorValue] = useState([]);
    const [productMaterialsData, setProductMaterialsData] = useState([]);
    const [productMaterials, setProductMaterials] = useState([]);
    const [materialsCategoryValueWithChilds, setMaterialsCategoryValueWithChilds] = useState([]);
    const [productModelType, setProductModelType] = useState([]);
    const [modelStatus, setModelStatus] = useState(window.location.pathname === '/artist_search_item' ? [] : [5]);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [appliedFiltersList, setAppliedFiltersList] = useState([]);
    const [, updateState] = React.useState();
    const forceUpdate = React.useCallback(() => updateState({}), []);
    const [offset, setOffset] = useState(0);
    const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
    const [products, setProducts] = useState([]);
    const [allDataLoaded, setAllDataLoaded] = useState(false);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [selectedAdd, setSelectedAdd] = useState([]);
    const [selectedRemove, setSelectedRemove] = useState([]);
    const [selectedElementDetials, setSelectedElementDetials] = useState([]);
    const [actionLoader, setActionLoader] = useState(false);
    const [visibleToArtist, setVisibleToArtist] = useState(false);
    const artistSearchItem = window.location.pathname === '/artist_search_item';
    const [isRestoringFilters, setIsRestoringFilters] = useState(true);
    const defaultModelStatus = artistSearchItem ? [] : [5];
    const [initialStatusInUrl, setInitialStatusInUrl] = useState(null);
    const currentRequestRef = useRef(null);

    useEffect(() => {
        setIsLoadingProducts(true);
        setProductCategoryData();
        loadProductMaterialsData();
        // capture whether status existed in the URL initially
        const params = new URLSearchParams(window.location.search);
        setInitialStatusInUrl(params.has('status'));
        // Don't call fetchProductsWrapper here - we'll call it after data is loaded
    }, []);
    
    // New useEffect to handle filter restoration after data is loaded
    useEffect(() => {
        // Only proceed if both category and material data are loaded
        if (productCategoriesData.length > 0 && productMaterialsData.length > 0) {
            fetchProductsWrapper(true); // Now safe to restore filters
        }
    }, [productCategoriesData, productMaterialsData]);

    const saveFiltersToURL = () => {
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get("tab");
        if (tabFromUrl !== 'product') {
            return;
        }
        
        // Save search value
        if (searchValue) {
            params.set("search", searchValue);
        } else {
            params.delete("search");
        }
        
        // Save category filter
        if (productCategoryValue.length > 0) {
            params.set("category", productCategoryValue.join(','));
        } else {
            params.delete("category");
        }
        
        // Save style category filter
        if (productStyleCategory.length > 0) {
            params.set("style_category", productStyleCategory.join(','));
        } else {
            params.delete("style_category");
        }
        
        // Save color filter
        if (colorValue.length > 0) {
            params.set("color", colorValue.join(','));
        } else {
            params.delete("color");
        }
        
        // Save material filter
        if (productMaterials.length > 0) {
            params.set("material", productMaterials.join(','));
        } else {
            params.delete("material");
        }
        
        // Save model type filter
        if (productModelType.length > 0) {
            params.set("model_type", productModelType.join(','));
        } else {
            params.delete("model_type");
        }
        
        // Save status filter
        // Avoid writing default status when it wasn’t present initially
        if (modelStatus.length > 0) {
            if (!(Array.isArray(defaultModelStatus) && modelStatus.length === defaultModelStatus.length && modelStatus.every((v, i) => v === defaultModelStatus[i])) || initialStatusInUrl) {
                params.set("status", modelStatus.join(','));
            } else {
                params.delete("status");
            }
        } else {
            params.delete("status");
        }
        
        // Save visible to artist filter
        if (visibleToArtist) {
            params.set("visible_to_artist", "true");
        } else {
            params.delete("visible_to_artist");
        }
        
        // Update URL
        window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    };

    const fetchProductsWrapper = (isInitialLoad = false) => {
        // Only restore filters from URL on initial load
        if (!isInitialLoad) {
            fetchProducts(true);
            return;
        }
        
        // Safety check: ensure required data is loaded before restoring filters
        if (productCategoriesData.length === 0 || productMaterialsData.length === 0) {
            console.log('Required data not yet loaded, skipping filter restoration');
            // Defer fetching until data is loaded to avoid a default-only request
            return;
        }
        
        // Check if we're on the product tab and restore search from URL params
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get("tab");
        const searchFromUrl = params.get("search");
        
        // Restore filters from URL params only on initial load
        const categoryFromUrl = params.get("category");
        const styleCategoryFromUrl = params.get("style_category");
        const colorFromUrl = params.get("color");
        const materialFromUrl = params.get("material");
        const modelTypeFromUrl = params.get("model_type");
        const statusFromUrl = params.get("status");
        const visibleFromUrl = params.get("visible_to_artist");
        
        if (tabFromUrl === 'product') {
            // Set flag to prevent infinite loop
            setIsRestoringFilters(true);
            
            // Batch all filter restorations to prevent multiple useEffect triggers
            const updates = [];
            
            // Restore search value
            if (searchFromUrl) {
                updates.push(() => setSearchValue(searchFromUrl));
            }
            
            // Restore category filter
            if (categoryFromUrl) {
                const categoryArray = categoryFromUrl.split(',');
                updates.push(() => {
                    setProductCategoryValue(categoryArray);
                    // Need to rebuild the withChilds array
                    let values = [];
                    categoryArray.forEach((name) => {
                        const targetItem = productParentCateogries.find(item => item.name === name);
                        if (targetItem) {
                            const result = findItemsByParentId(productParentCateogries, name, targetItem.category_id);
                            result.unshift(name);
                            values = [...values, ...result];
                        } else {
                            // If category not found in loaded data, just add the name itself
                            values.push(name);
                        }
                    });
                    setProductCategoryValueWithChilds(values);
                });
            }
            
            // Restore style category filter
            if (styleCategoryFromUrl) {
                const styleArray = styleCategoryFromUrl.split(',');
                updates.push(() => setProductStyleCategory(styleArray));
            }
            
            // Restore color filter
            if (colorFromUrl) {
                const colorArray = colorFromUrl.split(',');
                updates.push(() => setColorValue(colorArray));
            }
            
            // Restore material filter
            if (materialFromUrl) {
                const materialArray = materialFromUrl.split(',');
                updates.push(() => {
                    setProductMaterials(materialArray);
                    // Need to rebuild the withChilds array
                    let values = [];
                    materialArray.forEach((name) => {
                        const targetItem = productMaterialsData.find(item => item.title === name);
                        if (targetItem) {
                            const result = findChildrenByParentTitle(name, targetItem);
                            result.unshift(name);
                            values = [...values, ...result];
                        } else {
                            // If material not found in loaded data, just add the name itself
                            values.push(name);
                        }
                    });
                    setMaterialsCategoryValueWithChilds(values);
                });
            }
            
            // Restore model type filter
            if (modelTypeFromUrl) {
                const typeArray = modelTypeFromUrl.split(',');
                updates.push(() => setProductModelType(typeArray));
            }
            
            // Restore status filter
            if (statusFromUrl) {
                const statusArray = statusFromUrl.split(',').map(s => parseInt(s));
                updates.push(() => setModelStatus(statusArray));
            }
            // Note: If no statusFromUrl, keep the default modelStatus value (which is [5] for non-artist pages)
            
            // Restore visible to artist filter
            if (visibleFromUrl === 'true') {
                updates.push(() => setVisibleToArtist(true));
            }
            
            // Execute all updates at once
            updates.forEach(update => update());
            
            // Build overrides directly from URL to avoid initial fetch using defaults
            const overrides = {};
            if (categoryFromUrl) {
                const categoryArray = categoryFromUrl.split(',');
                let values = [];
                categoryArray.forEach((name) => {
                    const targetItem = productParentCateogries.find(item => item.name === name);
                    if (targetItem) {
                        const result = findItemsByParentId(productParentCateogries, name, targetItem.category_id);
                        result.unshift(name);
                        values = [...values, ...result];
                    } else {
                        values.push(name);
                    }
                });
                overrides.productCategoryValueWithChilds = values;
            }
            if (styleCategoryFromUrl) {
                overrides.productStyleCategory = styleCategoryFromUrl.split(',');
            }
            if (colorFromUrl) {
                overrides.colorValue = colorFromUrl.split(',');
            }
            if (materialFromUrl) {
                const materialArray = materialFromUrl.split(',');
                let values = [];
                materialArray.forEach((name) => {
                    const targetItem = productMaterialsData.find(item => item.title === name);
                    if (targetItem) {
                        const result = findChildrenByParentTitle(name, targetItem);
                        result.unshift(name);
                        values = [...values, ...result];
                    } else {
                        values.push(name);
                    }
                });
                overrides.materialsCategoryValueWithChilds = values;
                overrides.productMaterials = materialArray;
            }
            if (modelTypeFromUrl) {
                overrides.productModelType = modelTypeFromUrl.split(',');
            }
            if (statusFromUrl) {
                overrides.modelStatus = statusFromUrl.split(',').map(s => parseInt(s));
            }
            if (visibleFromUrl === 'true') {
                overrides.visibleToArtist = true;
            }
            
            // Fetch products with restored filters using overrides and search
            const initialSearch = searchFromUrl ? searchFromUrl : undefined;
            fetchProducts(true, initialSearch, overrides).finally(() => {
                setIsRestoringFilters(false);
            });
        } else {
            fetchProducts(true);
            setIsRestoringFilters(false);
        }
    }

    useEffect(() => {
        // Skip if we're currently restoring filters from URL
        if (isRestoringFilters) {
            return;
        }
        
        //display tags when this is clicked
        if (productCategoryValue.length > 0
            || (productStyleCategory.length > 0)
            || (colorValue.length > 0)
            || (productMaterials.length > 0)
            || (productModelType.length > 0)
            || (modelStatus.length > 0)
            || visibleToArtist) {
            setFiltersApplied(true);
            forceUpdate();
            setAppliedFiltersList(getAppliedFilters())
        } else {
            setFiltersApplied(false);
            setAppliedFiltersList([])
        }
    }, [productCategoryValue, productStyleCategory, colorValue, productMaterials, productModelType, modelStatus, visibleToArtist, isRestoringFilters])

    // Save filters to URL after restoration is complete
    useEffect(() => {
        // Only save to URL if we're not currently restoring filters
        if (!isRestoringFilters) {
            saveFiltersToURL();
        }
    }, [productCategoryValueWithChilds, productStyleCategory, colorValue, materialsCategoryValueWithChilds, productModelType, modelStatus, visibleToArtist, isRestoringFilters])

    // Trigger API call when filters change
    useEffect(() => {
        // Skip if we're currently restoring filters from URL
        if (isRestoringFilters) {
            return;
        }
        
        // Reset offset and products when filters change
        setOffset(0);
        setProducts([]);
        setAllDataLoaded(false);
        // Add a small delay to batch rapid filter changes and prevent race conditions
        const timeoutId = setTimeout(() => {
            fetchProducts(true);
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, [productCategoryValueWithChilds, productStyleCategory, colorValue, materialsCategoryValueWithChilds, productModelType, modelStatus, visibleToArtist])

    const getProductPayload = (customOffset = null, customSearchValue = null, overrides = null) => {
        let payload = {"required_fields": ["id","name","brand_id", "category", "height",  "customer_username",
        "width","depth", "model_status","thumbnail","last_modified_stamp", "variant_of",
        "is_store_item","group_id", "dimensions", "company_id", "color_name", "materials", 
        "platform", "style_category", "gtin", "tags","product_model_type", "created_on_stamp", "is_ai_model"
        , "artist_visible"]}
        let filterString = '(is_hidden__not=true&&company_id__notexact=1)'
        
        // Use window.location.pathname directly
        if (artistSearchItem) {
            filterString += '&&(artist_visible__exact=true)';
        }

        // Prefer override values on initial restoration
        const effectiveCategoryWithChilds = overrides && overrides.productCategoryValueWithChilds !== undefined
            ? overrides.productCategoryValueWithChilds
            : productCategoryValueWithChilds;
        const effectiveStyleCategory = overrides && overrides.productStyleCategory !== undefined
            ? overrides.productStyleCategory
            : productStyleCategory;
        const effectiveColorValue = overrides && overrides.colorValue !== undefined
            ? overrides.colorValue
            : colorValue;
        const effectiveMaterialsWithChilds = overrides && overrides.materialsCategoryValueWithChilds !== undefined
            ? overrides.materialsCategoryValueWithChilds
            : materialsCategoryValueWithChilds;
        const effectiveProductModelType = overrides && overrides.productModelType !== undefined
            ? overrides.productModelType
            : productModelType;
        const effectiveModelStatus = overrides && overrides.modelStatus !== undefined
            ? overrides.modelStatus
            : modelStatus;
        const effectiveVisibleToArtist = overrides && overrides.visibleToArtist !== undefined
            ? overrides.visibleToArtist
            : visibleToArtist;

        // Add category filter if categories are selected
        if (effectiveCategoryWithChilds.length > 0) {
            const categoryConditions = effectiveCategoryWithChilds.map(category => `category__exact='${category.replace(/'/g, "''")}'`).join('||');
            filterString += `&&(${categoryConditions})`;
        }

        // Add style category filter if style categories are selected
        if (effectiveStyleCategory.length > 0) {
            const styleConditions = effectiveStyleCategory.map(style => `style_category__exact='${style.replace(/'/g, "''")}'`).join('||');
            filterString += `&&(${styleConditions})`;
        }

        // Add color filter if colors are selected
        if (effectiveColorValue.length > 0) {
            const colorConditions = effectiveColorValue.map(color => `color_name__exact='${color.replace(/'/g, "''")}'`).join('||');
            filterString += `&&(${colorConditions})`;
        }

        // Add material filter if materials are selected
        if (effectiveMaterialsWithChilds.length > 0) {
            const materialConditions = effectiveMaterialsWithChilds.map(material => `materials__ilike='%${material.replace(/'/g, "''")}%'`).join('||');
            filterString += `&&(${materialConditions})`;
        }

        // Add product model type filter if types are selected
        if (effectiveProductModelType.length > 0) {
            const typeConditions = effectiveProductModelType.map(type => `product_model_type__exact='${type.replace(/'/g, "''")}'`).join('||');
            filterString += `&&(${typeConditions})`;
        }

        // Add model status filter if selected
        if (effectiveModelStatus.length > 0) {
            const statusConditions = effectiveModelStatus.map(status => `model_status__in=[${status}]`).join('||');
            filterString += `&&(${statusConditions})`;
        }

        // Add visible to artist filter if enabled
        if (effectiveVisibleToArtist) {
            filterString += '&&(artist_visible__exact=true)';
        }

        const effectiveSearchValue = customSearchValue !== null ? customSearchValue : searchValue;
        if (effectiveSearchValue) {
            // Sanitize and split searchValue
            const parts = effectiveSearchValue.replace(/[^a-zA-Z0-9' ]/g, " ").split(" ").filter(Boolean);
            if (parts.length > 0) {
                const nameSearchConditions = parts.map(part => `name__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `&&((${nameSearchConditions})`;
                const categorySearchConditions = parts.map(part => `category__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `||(${categorySearchConditions})`;
                // const colorSearchConditions = parts.map(part => `color_name__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                // filterString += `||(${colorSearchConditions})`;
                const brandSearchConditions = parts.map(part => `brand_id__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `||(${brandSearchConditions})`;

                // Only include parts that are valid integers for id search
                const idSearchParts = parts.filter(part => /^\d+$/.test(part));
                const idSearchConditions = idSearchParts.map(part => `id__exact='${part.replace(/'/g, "''")}'`).join('||');
                if (idSearchConditions) {
                    filterString += `||(${idSearchConditions})`;
                }
                filterString += ")";
            }
        }

        payload['filter_string'] = filterString
        payload['pagination_filters'] = {
            'limit': pageSize,
            'offset': customOffset !== null ? customOffset : offset
        }
        payload['order_by'] = 'last_modified_stamp desc'
        return payload;
    }

    const updateOffset = (offset) => {
        let newOffset = offset + pageSize;
        setOffset(newOffset);
    };

    const fetchProducts = (isInitialLoad = false, customSearchValue = null, overrides = null) => {
        // Cancel any existing request
        if (currentRequestRef.current) {
            currentRequestRef.current.cancel();
        }
        
        // Create a new cancel token for this request
        const cancelTokenSource = axios.CancelToken.source();
        currentRequestRef.current = cancelTokenSource;
        
        if (isInitialLoad) {
            setIsLoadingProducts(true);
            setOffset(0);
            // Use offset 0 for initial load
            let payload = getProductPayload(0, customSearchValue, overrides);
            return axios.post(ENVIRONMENT.LIST_PRODUCT, payload, {
                cancelToken: cancelTokenSource.token
            })
                .then(res => {
                    if (res.data) {
                        if (!res.data.length) {
                          setAllDataLoaded(true);
                          setIsLoadingMoreData(false);
                          setIsLoadingProducts(false);
                          return;
                        }
                        setAllDataLoaded(false);
                        // Add entity_type: 'product' and last_modified to each object in res.data
                        const productsWithEntityType = res.data.map(item => ({ 
                            ...item, 
                            entity_type: 'product',
                            last_modified: item.last_modified_stamp
                        }));
                        
                        // Replace products for initial load or filter changes
                        setProducts(productsWithEntityType);
                        console.log("*********", productsWithEntityType)
                        // Update offset after initial load so next "load more" uses correct offset
                        updateOffset(0);
                        setIsLoadingMoreData(false);
                        setIsLoadingProducts(false);
                    }
                })
                .catch(error => {
                    // Don't handle errors for cancelled requests
                    if (axios.isCancel(error)) {
                        console.log('Request cancelled');
                        return;
                    }
                    console.error('Error fetching products:', error);
                    setIsLoadingMoreData(false);
                    setIsLoadingProducts(false);
                });
        } else {
            setIsLoadingMoreData(true);
            // Use current offset for load more
            let payload = getProductPayload(undefined, customSearchValue, overrides);
            return axios.post(ENVIRONMENT.LIST_PRODUCT, payload, {
                cancelToken: cancelTokenSource.token
            })
                .then(res => {
                    if (res.data) {
                        if (!res.data.length) {
                          setAllDataLoaded(true);
                          setIsLoadingMoreData(false);
                          setIsLoadingProducts(false);
                          return;
                        }
                        setAllDataLoaded(false);
                        // Add entity_type: 'product' and last_modified to each object in res.data
                        const productsWithEntityType = res.data.map(item => ({ 
                            ...item, 
                            entity_type: 'product',
                            last_modified: item.last_modified_stamp
                        }));
                        
                        // Append products for "load more"
                        const tempProducts = [...products, ...productsWithEntityType];
                        setProducts(tempProducts);
                        updateOffset(offset);
                        setIsLoadingMoreData(false);
                        setIsLoadingProducts(false);
                    }
                })
                .catch(error => {
                    // Don't handle errors for cancelled requests
                    if (axios.isCancel(error)) {
                        console.log('Request cancelled');
                        return;
                    }
                    console.error('Error fetching products:', error);
                    setIsLoadingMoreData(false);
                    setIsLoadingProducts(false);
                });
        }
    }

    const setProductCategoryData = () => {
        let payload = {
            output: 'tree'
        };
        axios.post(ENVIRONMENT.ADMIN_CATEGORY_GET_BATCH, payload)
            .then(res => {
                setProductCategoriesData(res.data);
            });

        payload = {
            output: 'serial'
        };
        axios.post(ENVIRONMENT.ADMIN_CATEGORY_GET_BATCH, payload)
            .then(res => {
                setProductParentCateogries(res.data);
            });
    }

    const loadProductMaterialsData = () => {
        let data = [];
        axios.post(ENVIRONMENT.ADMIN_MATERIAL_BATCH, {
        })
            .then(res => {
                let categoriesData = Utilities.getMaterialsData(res.data);
                for (let i = 1; i <= (res.data).length; i++) {
                    if (res.data[i - 1].parent_id == "") {
                        data.push({
                            key: res.data[i - 1]['material_id'],
                            title: res.data[i - 1]['name'],
                            value: res.data[i - 1]['name'],
                            children: categoriesData[res.data[i - 1]['material_id']],
                        });
                    }
                }
                setProductMaterialsData(data);
            });
    }

    const changeSearchValue = (value) => {
        setSearchValue(value);
        // TODO: Implement search functionality
        console.log('Search value:', value);
    }

    const openFilters = () => {
        setFiltersDrawer(true);
    };

    const closeFiltersDrawer = () => {
        setFiltersDrawer(false);
    }

    const changeProductCategory = (value) => {
        let values = []

        value.map((name) => {
            const targetItemName = name;
            const targetItem = productParentCateogries.find(item => item.name === targetItemName);

            if (targetItem) {
                const result = findItemsByParentId(productParentCateogries, targetItemName, targetItem.category_id);
                result.unshift(name);
                values = [...values, ...result]
            }
        });

        setProductCategoryValue(value);
        setProductCategoryValueWithChilds(values);
    }

    const changeProductStyleCategory = (value) => {
        setProductStyleCategory(value);
    }

    const changeColorValue = (value) => {
        setColorValue(value);
    }

    const changeProductMaterial = (value) => {
        let values = []

        value.map((name) => {
            const targetItemTitle = name;
            const targetItem = productMaterialsData.find(item => item.title === targetItemTitle);
            if (targetItem) {
                const result = findChildrenByParentTitle(targetItemTitle, targetItem);
                result.unshift(name);
                values = [...values, ...result]
            }
        });

        if (values.length <= 0) {
            values = value
        }

        setProductMaterials(value);
        setMaterialsCategoryValueWithChilds(values);
    }

    const changeProductModelType = (value) => {
        setProductModelType(value);
    }

    const changeModelStatus = (value) => {
        setModelStatus(value);
    }

    const changeProductSearchCategory = (value) => {
        setCategorySearchValue(value);
    }

    const findItemsByParentId = (data, targetItemName, parentId) => {
        const items = [];
        for (const item of data) {
            if (item.parent_id === parentId) {
                items.push(item.name);
                items.push(...findItemsByParentId(data, targetItemName, item.category_id));
            }
        }
        return items;
    }

    const findChildrenByParentTitle = (parentTitle, node) => {
        if (node.title === parentTitle) {
            return node.children.map(child => child.title);
        } else if (node.children) {
            let result = [];
            for (const childNode of node.children) {
                result = result.concat(findChildrenByParentTitle(parentTitle, childNode));
            }
            return result;
        } else {
            return [];
        }
    }

    const getAppliedFilters = () => {
        let appliedFilters = [...productCategoryValueWithChilds, ...productStyleCategory, ...colorValue,
            ...materialsCategoryValueWithChilds, ...productModelType, ...modelStatus]
        if (visibleToArtist) {
            appliedFilters.push('visible_to_artist');
        }
        appliedFilters = appliedFilters.map(item => item.toString().toLowerCase())
        return appliedFilters;
    }

    const closeTags = (entity, type) => {
        //remove the tag when this is clicked from showing result section
        if (type == 'product_category') {
            let arr = productCategoryValue.filter(category => { return entity != category });
            changeProductCategory(arr)
        } else if (type == 'product_style_category') {
            let arr = productStyleCategory.filter(style_category => { return entity != style_category });
            setProductStyleCategory(arr);
        } else if (type == 'product_color') {
            let arr = colorValue.filter(color => { return entity != color });
            setColorValue(arr);
        } else if (type == 'product_material') {
            let arr = productMaterials.filter(material => { return entity != material });
            setProductMaterials(arr);
            let arr2 = materialsCategoryValueWithChilds.filter(material => { return entity != material });
            setMaterialsCategoryValueWithChilds(arr2);
        } else if (type == 'product_model_type') {
            let arr = productModelType.filter(model => { return entity != model });
            setProductModelType(arr);
        } else if (type == 'model_status') {
            let arr = modelStatus.filter(status => { return entity != status });
            setModelStatus(arr);
        } else if (type == 'visible_to_artist') {
            setVisibleToArtist(false);
        }
    }

    const resetFilters = () => {
        productCategoryValue.length > 0 && productCategoryValue.map((category) => {
            closeTags(category, 'product_category');
        });
        productStyleCategory.length > 0 && productStyleCategory.map((category) => {
            closeTags(category, 'product_style_category');
        });
        colorValue.length > 0 && colorValue.map((category) => {
            closeTags(category, 'product_color')
        })
        productMaterials.length > 0 && productMaterials.map((category) => {
            closeTags(category, 'product_material')
        })
        productModelType.length > 0 && productModelType.map((category) => {
            closeTags(category, 'product_model_type');
        })
        modelStatus.length > 0 && modelStatus.map((status) => {
            closeTags(status, 'model_status');
        })
        visibleToArtist && closeTags('visible_to_artist', 'visible_to_artist');
        
        // Save cleared filters to URL
        saveFiltersToURL();
    };

    const getFilteredTags = () => {
        let filteredTags = <>
            {filtersApplied && <Col span={24} style={{ marginLeft: 10, marginTop: 20, textAlign: "left" }}>
                <h4 className='manrope f-14 black-55 italic'>Showing results for</h4>
                <div style={{ marginBottom: 20 }}>
                    {
                        productCategoryValue.length > 0 && productCategoryValue.map((category) => {
                            return <Tag onClose={() => closeTags(category, 'product_category')}
                                closable visible={true}
                                className='manrope f-12 black-55 tag-filter'>Category: <span className='manrope f-12 grey-77'>{category}</span></Tag>
                        })}

                    {productStyleCategory.length > 0 && productStyleCategory.map((category) => {
                        return <Tag onClose={() => closeTags(category, 'product_style_category')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Style Category: <span className='manrope f-12 grey-77'>{category}</span></Tag>
                    })}

                    {colorValue.length > 0 && colorValue.map((category) => {
                        return <Tag onClose={() => closeTags(category, 'product_color')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Color: <span className='manrope f-12 grey-77'>{category}</span></Tag>
                    })}

                    {
                        productMaterials.length > 0 && productMaterials.map((category) => {
                            return <Tag onClose={() => closeTags(category, 'product_material')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Material: <span className='manrope f-12 grey-77'>{category}</span></Tag>
                        })}

                    {
                        productModelType.length > 0 && productModelType.map((category) => {
                            return <Tag onClose={() => closeTags(category, 'product_model_type')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Product Type: <span className='manrope f-12 grey-77'>{FileConstants.PRODUCT_MODEL_TYPE[category]}</span></Tag>
                        })}

                    {
                        modelStatus.length > 0 && modelStatus.map((status) => {
                            return <Tag onClose={() => closeTags(status, 'model_status')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Model Status: <span className='manrope f-12 grey-77'>{FileConstants.MODEL_STATUS_STRING[status]}</span></Tag>
                        })}

                    {visibleToArtist && (
                        <Tag onClose={() => closeTags('visible_to_artist', 'visible_to_artist')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Visible to Artist</Tag>
                    )}
                </div>
            </Col>}
        </>

        return filteredTags;
    }

    // Add onLoadMore handler
    const handleLoadMore = () => {
        if (!isLoadingMoreData && !allDataLoaded) {
            fetchProducts();
        }
    };

    const handleSearchIconClick = () => {
        if(searchValue && searchValue.trim() !== '')
        {
            setOffset(0);
            setProducts([]);
            setAllDataLoaded(false);
            fetchProducts(true);
            // Save all filters including search to URL
            saveFiltersToURL();
        }
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setOffset(0);
        setProducts([]);
        setAllDataLoaded(false);
        fetchProducts(true, ''); // Pass empty string as search value
        
        // Remove search parameter from URL
        const params = new URLSearchParams(window.location.search);
        params.delete("search");
        window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    };

    useEffect(() => {
        console.log(selectedAdd, selectedRemove)
    }, [selectedAdd, selectedRemove])

    const handleAddToArtistSearch = () => {
        setActionLoader(true);
        let payload = {
            "add_ids": selectedAdd,
            "remove_ids": selectedRemove,
            "entity": "product"
        }

        axios.post(ENVIRONMENT.UPDATE_ARTIST_VISIBLE, payload)
            .then(res => {
                console.log(res);
                setActionLoader(false);
                message.success('Products added to artist search');
                window.location.reload();
            })
            .catch(error => {
                console.error('Error adding to artist search:', error);
                setActionLoader(false);
            });
    }

    return (
        <>
        { isLoadingProducts && offset == 0 ? 
            <div className="justify-in-center">
                <DottedLoader />
            </div>
        :
        <div className="product-search">
            <Row className="justify-space-between" style={{ marginTop: "20px", marginBottom: 20 }}>
                <Col lg={10} xl={10}>
                    <div className='manrope f-24 w-700' style={{ display: 'flex', alignItems: "flex-end" }}>
                        Product Library
                    </div>
                </Col>
                <Col lg={14} xl={14} className="justify-in-end">
                    <div className="search-bar-width mr-8">
                        <Form>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <Input 
                                    placeholder="Search All Products" 
                                    value={searchValue} 
                                    onChange={e => { changeSearchValue(e.target.value); }} 
                                    className="manrope f-14 mat-search-bar custom-input-id"
                                    style={{ paddingRight: searchValue ? '84px' : '48px' }}
                                    onPressEnter={handleSearchIconClick}
                                />
                                {searchValue && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: 42,
                                            top: 3,
                                            height: '87%',
                                            width: '42px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            zIndex: 11,
                                            background: '#fff',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                        }}
                                        onClick={handleClearSearch}
                                    >
                                        <CloseCircleFilled style={{ color: '#aaa', fontSize: 18 }} />
                                    </div>
                                )}
                                <div className="suffix-container" style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#276DD7', borderTopRightRadius: '4px', borderBottomRightRadius: '4px', cursor: 'pointer', zIndex: 10 }} onClick={handleSearchIconClick}>
                                    <ArrowRightOutlined className="custom-icon" />
                                </div>
                            </div>
                        </Form>
                    </div>
                    <div>
                        <Tooltip title={<span className="manrope f-12 white">Apply Filters</span>}>
                            <span>
                                <div className={"icon-button-container mr-8"} onClick={openFilters}>
                                    <FilterOutlined />
                                </div>
                            </span>
                        </Tooltip>
                    </div>
                </Col>
            </Row>
            {getFilteredTags()}
            <ProductsList 
                data={products} 
                onLoadMore={handleLoadMore} 
                searchMode={true} 
                selectable={true}
                selected={selectedAdd}
                setSelected={setSelectedAdd}
                setSelectedElementDetials={setSelectedElementDetials}
                selectedRemove={selectedRemove}
                setSelectedRemove={setSelectedRemove}
                artistSearch={artistSearchItem}
            />

            {/* Bottom menu logic */}
            {selectedAdd.length > 0 || selectedRemove.length > 0 ? (
                <div className="bottom-menu" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Selected count on the left */}
                    <div className='manrope black-d9 f-16 ml-168' style={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleFilled className='mr-2' />   {selectedAdd.length > 0 ? `${selectedAdd.length} model(s) selected` : ''} {selectedAdd.length > 0 && selectedRemove.length > 0 ? ', ' : ''} {selectedRemove.length > 0 ? `${selectedRemove.length} model(s) removed` : ''}
                    </div>
                    {/* Loading message in the center if loading more */}
                    {isLoadingMoreData && (
                        <div className='manrope f-16' style={{ color: '#276DD7', padding: 10, display: 'flex', alignItems: 'center' }}>
                            <div className="more-item-loading-container justify-in-center">
                                <Spin indicator={antIcon} tip="Loading more products" />
                            </div>
                        </div>
                    )}
                    {/* Button on the right */}
                    <Button
                        className="product-btn approve-button f-14 ml-16 br-4"
                        onClick={handleAddToArtistSearch}
                    >
                        {actionLoader ? <LoadingOutlined /> : ""}
                        Save Changes to Artist Search
                    </Button>
                </div>
            ) : null}
            {/* If no products are selected, keep the current loading more message */}
            {selectedAdd.length === 0 && isLoadingMoreData && (
                <div className="more-item-loading-container justify-in-center">
                    <Spin indicator={antIcon} tip="Loading more products" />
                </div>
            )}
        </div>}
        <Drawer
                width={360}
                visible={filtersDrawer}
                onClose={closeFiltersDrawer}
                placement="right">
                <div style={{ padding: 20 }}>
                    <h2 className="manrope f-18 black-14 w-700">Filters</h2>
                    <div>
                        <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>
                            Product Category
                        </h5>
                        <TreeSelect
                            value={productCategoryValue}
                            showSearch={true}
                            className="tree-select-material f-14 filter bg-light"
                            style={{ width: '100%' }}
                            multiple
                            dropdownStyle={categoryStyle}
                            placeholder="Search and select category"
                            treeData={productCategoriesData}
                            onChange={changeProductCategory}
                            onSearch={changeProductSearchCategory} />

                        <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Style Category</h5>
                        <StyleCategory
                            className="tree-select-material f-14 filter bg-light"
                            onChange={changeProductStyleCategory}
                            value={productStyleCategory}
                            placeholder="Search and select style category"
                            mode="multiple" />

                        <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Product Color</h5>
                        <ColorSelect
                            className="tree-select-material f-14 filter bg-light"
                            value={colorValue}
                            onChange={changeColorValue}
                            placeholder="Search and select color"
                            mode="multiple" />

                        <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Product Material</h5>
                        <TreeSelect
                            value={productMaterials}
                            showSearch={true}
                            className="tree-select-material f-14 filter bg-light"
                            style={{ width: '100%' }}
                            multiple
                            dropdownStyle={categoryStyle}
                            placeholder="Search and select material"
                            treeData={productMaterialsData}
                            onChange={changeProductMaterial}
                            onSearch={changeProductSearchCategory}
                        >
                        </TreeSelect>
                        <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Product Type</h5>
                        <Select
                            className="tree-select-material f-14 filter bg-light"
                            value={productModelType}
                            onChange={changeProductModelType}
                            showSearch
                            placeholder="Search and select product type"
                            mode="multiple"
                        >
                            {Object.keys(FileConstants.PRODUCT_MODEL_TYPE).map((key) => (
                                <Select.Option
                                    key={key}
                                    className="manrope f-10"
                                    value={key}
                                >
                                    {FileConstants.PRODUCT_MODEL_TYPE[key]}
                                </Select.Option>
                            ))}
                        </Select>
                        {
                            !artistSearchItem &&
                            <>
                            <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Model Status</h5>
                                <Select
                                    className="tree-select-material f-14 filter bg-light"
                                    value={modelStatus}
                                    onChange={changeModelStatus}
                                    showSearch
                                    placeholder="Search and select model status"
                                    mode="multiple"
                                >
                                    {Object.keys(FileConstants.MODEL_STATUS_STRING).map((key) => (
                                        <Select.Option
                                            key={key}
                                            className="manrope f-10"
                                            value={parseInt(key)}
                                        >
                                            {FileConstants.MODEL_STATUS_STRING[key]}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Visible to Artist</h5>
                                <Switch
                                    checked={visibleToArtist}
                                    onChange={setVisibleToArtist}
                                />
                            </>
                        }
                    </div>
                </div>
            </Drawer>
        </>
    )
}

export default ProductSearch;